// Pre-existing helpers (`isPageNotFoundError`, `redirectPath`) — the data-layer
// migration only adds two route entries to this file.
// fallow-ignore-file complexity
import { activeRouteStore, getAttemptedTransitionUrl, setAttemptedTransitionUrl } from '@customerio/hydra-nav-state';
import { useStore } from '@nanostores/react';
import { useQueryClient } from '@tanstack/react-query';
import React, { Suspense, useEffect, useMemo } from 'react';
import type { LoaderFunctionArgs } from 'react-router-dom';
import { Outlet, redirect, useLocation, useParams, useRouteError } from 'react-router-dom';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { ConnectedPlumaProvider } from '@/components/connected-pluma-provider';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useAuthQuery } from '@/services/auth';
import { getSampleAccounts } from '@/features/workspaces';
import { generatePath } from '@/services/router/generate-path';
import { handleWorkspaceTransition } from '@/services/db/shared/workspace-cache-sync';
import { isEmptyString } from '@/utils/string';
import Authenticated from '@/layouts/authenticated';
import EmbedRoot from '@/layouts/embed-root';
import Root, { queryClient } from '@/layouts/root';
import Index from '@/pages/index.page';
import DesignStudio, { DesignStudioNonBackgroundApp } from '@/pages/design-studio';
import ErrorPage from '@/pages/error.page';
import Journeys, { JourneysNonBackgroundApp } from '@/pages/journeys';
import NotFoundPage from '@/pages/not-found.page';
import { AppBundle, AppBundleRoutes } from '@/utils/app-bundles';
import { PageNotFoundError } from '@/utils/errors';
import { sentryCaptureException, sentryCaptureLegacyRedirect, sentryCreateBrowserRouter } from '@/utils/sentry';
import { PageLoader } from '@customerio/pluma-components/react';
import { ProtectedRoute } from './components/protected-route';
import { AiEnabledGuard, AiNotEnabledFallback, FeatureFlagGuard, PlanGuard } from '@/components/guards';
import { BUILDER_PLAN_SLUG } from '@/services/auth/plan-slugs';
import { AccountFeatureFlag } from '@/services/auth/feature-flags';
import { ThemeContextProvider } from '@/context/theme';
import { lazyRoute, installLinkPrefetching } from '@/services/router/prefetch';

// ── Lazy-loaded features ─────────────────────────────────────────────────────
const AssetLibrary = React.lazy(() => import('@/features/asset-library').then((m) => ({ default: m.AssetLibrary })));

// ── Lazy-loaded pages (prefetched on link hover) ────────────────────────────
const OAuth2 = lazyRoute('/oauth2/*', () => import('@/pages/oauth2.page'));
const AIBusinessProfile = lazyRoute(
  '/workspaces/:workspaceId/settings/ai-business-profile',
  () => import('@/pages/workspaces/settings/ai-business-profile/index.page'),
);
const WorkspaceAuditLogsPage = lazyRoute(
  '/workspaces/:workspaceId/settings/audit-logs',
  () => import('@/pages/workspaces/settings/audit-logs.page'),
);
const WorkspaceSuppressionsPage = lazyRoute(
  '/workspaces/:workspaceId/settings/suppressions',
  () => import('@/pages/workspaces/settings/suppressions/index.page'),
);
const WorkspaceAutoArchivePage = lazyRoute(
  '/workspaces/:workspaceId/settings/auto_archive',
  () => import('@/pages/workspaces/settings/auto-archive/index.page'),
);
const SinchSettingsPage = lazyRoute(
  '/workspaces/:workspaceId/settings/actions/sinch',
  () => import('@/pages/workspaces/settings/actions/sinch/index.page'),
);
const AuditLogsPage = lazyRoute('/settings/audit-logs', () => import('@/pages/settings/audit-logs.page'));
const DeliverabilityPage = lazyRoute(
  '/workspaces/:workspaceId/analysis/deliverability',
  () => import('@/pages/workspaces/analysis/deliverability/index.page'),
);

// ── Agent pages ─────────────────────────────────────────────────────────────
const Agent = lazyRoute('/workspaces/:workspaceId/agent', () => import('@/pages/workspaces/agent/index.page'));
const RoutinesPage = lazyRoute(
  '/workspaces/:workspaceId/agent/routines',
  () => import('@/pages/workspaces/agent/routines/index.page'),
);
const SkillsPage = lazyRoute(
  '/workspaces/:workspaceId/agent/skills',
  () => import('@/pages/workspaces/agent/skills/index.page'),
);
const SkillDetailPage = lazyRoute(
  '/workspaces/:workspaceId/agent/skills/:skillPath',
  () => import('@/pages/workspaces/agent/skills/skill-detail.page'),
);
const NewSkillPage = lazyRoute(
  '/workspaces/:workspaceId/agent/skills/new',
  () => import('@/pages/workspaces/agent/skills/new-skill.page'),
);

// ── Other workspace pages ───────────────────────────────────────────────────
const HealthPage = lazyRoute('/workspaces/:workspaceId/health', () => import('@/pages/workspaces/health/index.page'));
const HomePage = lazyRoute('/workspaces/:workspaceId/home', () => import('@/pages/workspaces/home/index.page'));
const AddPeoplePage = lazyRoute(
  '/workspaces/:workspaceId/home/add-people',
  () => import('@/pages/workspaces/home/add-people/index.page'),
);
const AssetsPage = lazyRoute('/workspaces/:workspaceId/assets', () => import('@/pages/workspaces/assets/index.page'));
// SnippetsLayout and SnippetsPage share the same pattern intentionally —
// the layout wraps the page, so both chunks are prefetched together.
const SnippetsLayout = lazyRoute(
  '/workspaces/:workspaceId/snippets',
  () => import('@/pages/workspaces/snippets/layout.page'),
);
const SnippetsPage = lazyRoute(
  '/workspaces/:workspaceId/snippets',
  () => import('@/pages/workspaces/snippets/index.page'),
);
const NewSnippetPage = lazyRoute(
  '/workspaces/:workspaceId/snippets/new',
  () => import('@/pages/workspaces/snippets/new/index.page'),
);
const SnippetDetailPage = lazyRoute(
  '/workspaces/:workspaceId/snippets/:snippetId',
  () => import('@/pages/workspaces/snippets/[snippet-id]/index.page'),
);
const CliPage = lazyRoute('/cli', () => import('@/pages/cli/index.page'));
const AccountApiKeysPage = lazyRoute('/settings/api-keys', () => import('@/pages/settings/api-keys/index.page'));
const WorkspaceApiKeysPage = lazyRoute(
  '/workspaces/:workspaceId/settings/api-keys',
  () => import('@/pages/settings/api-keys/workspace-page'),
);
const DnsSetupPage = lazyRoute('/cli/dns-setup', () => import('@/pages/cli/dns-setup/index.page'));

// ── Geofence pages ──────────────────────────────────────────────────────────
const GeofencesIndexPage = lazyRoute(
  '/workspaces/:workspaceId/geofences',
  () => import('@/pages/workspaces/geofences/index.page'),
);
const GeofencesCreatePage = lazyRoute(
  '/workspaces/:workspaceId/geofences/new',
  () => import('@/pages/workspaces/geofences/create.page'),
);
const GeofencesEditPage = lazyRoute(
  '/workspaces/:workspaceId/geofences/:geofenceId',
  () => import('@/pages/workspaces/geofences/edit.page'),
);

// ── Pipeline pages ───────────────────────────────────────────────────────────
const PipelineSourcesJourneysPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/sources/journeys',
  () => import('@/pages/workspaces/pipelines/sources/journeys.page'),
);
const PipelineAddSourcePage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/sources/add',
  () => import('@/pages/workspaces/pipelines/sources/add/index.page'),
);
const PipelineAddSourceOptionPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/sources/add/:sourceOptionId',
  () => import('@/pages/workspaces/pipelines/sources/add/[source-option-id].page'),
);
const PipelineSourceLayoutPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/sources/:sourceId',
  () => import('@/pages/workspaces/pipelines/sources/[source-id]/layout.page'),
);
const PipelineSourceIndexPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/sources/:sourceId',
  () => import('@/pages/workspaces/pipelines/sources/[source-id]/index.page'),
);
const PipelineSourceDataInPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/sources/:sourceId/data-in',
  () => import('@/pages/workspaces/pipelines/sources/[source-id]/data-in/index.page'),
);
const PipelineSourceSyncRunsPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/sources/:sourceId/imports',
  () => import('@/pages/workspaces/pipelines/sources/[source-id]/sync-runs.page'),
);
const PipelineSourceSyncsPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/sources/:sourceId/syncs',
  () => import('@/pages/workspaces/pipelines/sources/[source-id]/syncs.page'),
);
const PipelineSourceDatabasesPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/sources/:sourceId/databases',
  () => import('@/pages/workspaces/pipelines/sources/[source-id]/databases.page'),
);
const PipelineSourceSettingsPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/sources/:sourceId/settings',
  () => import('@/pages/workspaces/pipelines/sources/[source-id]/settings.page'),
);
const PipelineSourceAddSyncPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/sources/:sourceId/syncs/add',
  () => import('@/pages/workspaces/pipelines/sources/[source-id]/add-sync.page'),
);
const PipelineDestinationsJourneysPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/destinations/journeys',
  () => import('@/pages/workspaces/pipelines/destinations/journeys.page'),
);
const PipelineAddDestinationPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/destinations/add',
  () => import('@/pages/workspaces/pipelines/destinations/add/index.page'),
);
const PipelineAddDestinationOptionPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/destinations/add/:destinationOptionId',
  () => import('@/pages/workspaces/pipelines/destinations/add/[destination-option-id].page'),
);
const PipelineDestinationLayoutPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/destinations/:destinationId',
  () => import('@/pages/workspaces/pipelines/destinations/[destination-id]/layout.page'),
);
const PipelineDestinationIndexPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/destinations/:destinationId',
  () => import('@/pages/workspaces/pipelines/destinations/[destination-id]/index.page'),
);
const PipelineDestinationDataOutPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/destinations/:destinationId/data-out',
  () => import('@/pages/workspaces/pipelines/destinations/[destination-id]/data-out/index.page'),
);
const PipelineDestinationExportsPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/destinations/:destinationId/exports',
  () => import('@/pages/workspaces/pipelines/destinations/[destination-id]/exports.page'),
);
const PipelineDestinationTesterPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/destinations/:destinationId/tester',
  () => import('@/pages/workspaces/pipelines/destinations/[destination-id]/tester.page'),
);
const PipelineDestinationSettingsPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/destinations/:destinationId/settings',
  () => import('@/pages/workspaces/pipelines/destinations/[destination-id]/settings.page'),
);
const PipelineDestinationSubscriptionsIndexPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/destinations/:destinationId/subscriptions',
  () => import('@/pages/workspaces/pipelines/destinations/[destination-id]/subscriptions/index.page'),
);
const PipelineDestinationSubscriptionsAddPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/destinations/:destinationId/subscriptions/add',
  () => import('@/pages/workspaces/pipelines/destinations/[destination-id]/subscriptions/add.page'),
);
const PipelineDestinationSubscriptionPage = lazyRoute(
  '/workspaces/:workspaceId/pipelines/destinations/:destinationId/subscriptions/:subscriptionId',
  () => import('@/pages/workspaces/pipelines/destinations/[destination-id]/subscriptions/[subscription-id].page'),
);

// ── Helper components ────────────────────────────────────────────────────────

interface LastWorkspaceResolverProps {
  appBundle?: AppBundle;
  children: React.ReactNode;
}

/**
 * Layout-only parent for the `/workspaces/:workspaceId` subtree.
 *
 * Owns the workspace-switch lifecycle: a `useEffect` keyed on
 * `:workspaceId` runs `handleWorkspaceTransition`, which drops the
 * previous workspace's cache slots (mirrors Ember's
 * `environment/route.ts` `beforeModel` + `unloadModels`).
 *
 * **Why an effect, not a route loader.** The loader runs synchronously
 * during the navigation transition — *before* React has committed the
 * new workspace's component tree. At that moment the previous
 * workspace's components are still mounted with their observers
 * attached to the old query keys. `removeQueries` would notify those
 * still-active observers that their data is gone, triggering N parallel
 * refetches of the *previous* workspace's data on its way out. Running
 * the clear in `useEffect` defers it until after React commits: the new
 * workspace's components are mounted with their new observers, the old
 * observers have rebinded, and `removeQueries` has nothing to notify.
 *
 * Renders the matched child via `<Outlet />`; no chrome of its own.
 */
function WorkspaceRoot() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wsId = parseWorkspaceIdParam(workspaceId);
  const qc = useQueryClient();
  useEffect(() => {
    handleWorkspaceTransition(qc, wsId);
  }, [qc, wsId]);
  return (
    <>
      <Outlet />
      <Suspense>
        <AssetLibrary />
      </Suspense>
    </>
  );
}

/**
 * Coerce the `:workspaceId` route param to a positive integer or null.
 * 'last' (the LastWorkspaceResolver redirect token) and any other
 * non-numeric value resolves to null so the workspace-transition cursor
 * doesn't get polluted while LastWorkspaceResolver redirects to the real
 * id (the loader will fire again with the resolved param).
 */
function parseWorkspaceIdParam(workspaceId: string | undefined): number | null {
  if (workspaceId == null) return null;
  const n = Number(workspaceId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function JourneysWithAuthRefresh() {
  const auth = useAuthQuery();
  const location = useLocation();
  const activeRoute = useStore(activeRouteStore);

  useEffect(() => {
    if (auth.data == null && getAttemptedTransitionUrl() == null && window.location.pathname.startsWith('/login')) {
      setAttemptedTransitionUrl(window.location.origin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only needs to re-run when location changes
  }, [location]);

  useEffect(() => {
    if (!isEmptyString(activeRoute) && location.pathname !== window.location.pathname) {
      auth.refetch();
    }
  }, [activeRoute, location, auth]);

  return <JourneysNonBackgroundApp />;
}

function LastWorkspaceResolver({ appBundle, children }: LastWorkspaceResolverProps) {
  const { workspaceId } = useParams();
  const { pathname, search } = useLocation();
  const currentWorkspace = useCurrentWorkspace();

  const redirectPath = useMemo(() => {
    if (workspaceId !== 'last') {
      return null;
    }

    if (currentWorkspace == null) {
      return null;
    }

    if (appBundle != null) {
      const appPath = AppBundleRoutes[appBundle];
      const appPathWithSlash = `${appPath}/`;
      const appPathIndex = pathname.indexOf(appPathWithSlash);
      const subPath = appPathIndex !== -1 ? pathname.slice(appPathIndex + appPathWithSlash.length) : '';
      // eslint-disable-next-line local-rules/enforce-generate-path -- dynamic URL construction from app bundle path
      return `/workspaces/${currentWorkspace.id}${appPath}/${subPath}${search}`;
    } else {
      // eslint-disable-next-line local-rules/enforce-generate-path -- dynamic URL replacement
      return pathname.replace('/workspaces/last', `/workspaces/${currentWorkspace.id}`) + search;
    }
  }, [workspaceId, currentWorkspace, pathname, search, appBundle]);

  if (redirectPath != null) {
    return <SafeNavigate to={redirectPath} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return children;
}

// ── Legacy redirects ─────────────────────────────────────────────────────────

const preHydraJourneysEnvRegex = /^\/env\/(last|(\d+))((\/.*$)|$)/;
const v1HydraJourneysRegex = /^\/journeys\/env\/(last|(\d+))((\/.*$)|$)/;
const oldParcelRegex = /^\/workspaces\/(\d+)\/parcel((\/.*$)|$)/;

const REDIRECTS = [
  {
    from: 'workspaces/:workspaceId/journeys/analysis/deliverability',
    to: '/workspaces/:workspaceId/analysis/deliverability',
  },
  {
    from: 'workspaces/:workspaceId/journeys/tasks',
    to: '/workspaces/:workspaceId/health?tab=data&scrollTo=tasks',
  },
  {
    from: 'workspaces/:workspaceId/tasks',
    to: '/workspaces/:workspaceId/health?tab=data&scrollTo=tasks',
  },
  {
    from: 'workspaces/:workspaceId/home/workspace-setup',
    to: '/workspaces/:workspaceId/home',
  },
  {
    from: 'workspaces/:workspaceId/home/workspace-setup/add-people',
    to: '/workspaces/:workspaceId/home/add-people',
  },
  // Snippets used to live under `/journeys/snippets` (handed off to the Ember
  // bundle). They're Hydra-owned now at `/snippets` — keep the old URLs
  // working so bookmarks and inbound links from Ember tests/docs still land.
  // Assets used to live under `/journeys/assets` (Ember bundle).
  // Now Hydra-owned at `/assets`.
  {
    from: 'workspaces/:workspaceId/journeys/assets',
    to: '/workspaces/:workspaceId/assets',
  },
  {
    from: 'workspaces/:workspaceId/journeys/snippets',
    to: '/workspaces/:workspaceId/snippets',
  },
  {
    from: 'workspaces/:workspaceId/journeys/snippets/new',
    to: '/workspaces/:workspaceId/snippets/new',
  },
  {
    from: 'workspaces/:workspaceId/journeys/snippets/:snippetId',
    to: '/workspaces/:workspaceId/snippets/:snippetId',
  },
];

/**
 * Redirects legacy /pipelines/workspaces/:id/... URLs to /workspaces/:id/pipelines/...
 */
function PipelinesLegacyRedirect() {
  const { workspaceId, '*': rest } = useParams();
  // eslint-disable-next-line local-rules/enforce-generate-path -- dynamic legacy redirect
  const target = `/workspaces/${workspaceId}/pipelines/${rest ?? ''}`;
  return <SafeNavigate to={target} options={{ shouldReplaceHistoryEntry: true }} />;
}

type RedirectRegexToTargetAppProps = { regex: RegExp; targetApp: AppBundle };

function RedirectRegexToTargetApp({ regex, targetApp }: RedirectRegexToTargetAppProps) {
  let path = `${window.location.pathname}${window.location.search}`;
  const matches = path.match(regex);

  if (matches != null) {
    const [, workspaceId, _, rest] = matches;
    const adjustedRest = rest != null ? rest.replace(/^\//, '') : '';
    // eslint-disable-next-line local-rules/enforce-generate-path -- dynamic redirect from regex match
    path = `/workspaces/${workspaceId}`;

    path = adjustedRest.startsWith('settings')
      ? `${path}/${adjustedRest}`
      : `${path}${AppBundleRoutes[targetApp]}/${adjustedRest}`;
  } else {
    // eslint-disable-next-line local-rules/enforce-generate-path -- dynamic redirect with search params
    path = `/workspaces${window.location.search}`;
  }

  return <SafeNavigate to={path} options={{ shouldReplaceHistoryEntry: true }} />;
}

const journeysLegacyUrls = [
  '/oauth/*',
  '/integrations/*',
  '/vwb-demo/*',
  '/users/*',
  '/account_canceled/*',
  '/account_cancelled/*',
  '/account/cancelled/*',
  '/post_cancel/*',
];

const journeysLegacyUrlsWithAuthRefresh = [
  '/reset_password/*',
  '/login/*',
  '/signup/*',
  '/post_registration/*',
  '/confirm_account/*',
  '/account/*',
  '/confirm_account_notice/*',
  '/2fa_setup/*',
  '/no_accessible_workspaces/*',
  '/har-sanitizer/*',
  '/academy-login',
  '/checkout',
];

// ── Error handling ───────────────────────────────────────────────────────────

function isPageNotFoundError(error: unknown): boolean {
  if (error instanceof PageNotFoundError) {
    return true;
  }

  if (typeof error === 'object' && error != null && 'status' in error && error.status === 404) {
    return true;
  }

  return false;
}

const RootError = () => {
  const error = useRouteError();

  useEffect(() => {
    if (isPageNotFoundError(error)) {
      return;
    }

    sentryCaptureException(error);
  }, [error]);

  if (isPageNotFoundError(error)) {
    return (
      <ThemeContextProvider>
        <ConnectedPlumaProvider>
          <NotFoundPage />
        </ConnectedPlumaProvider>
      </ThemeContextProvider>
    );
  }

  return (
    <ThemeContextProvider>
      <ConnectedPlumaProvider>
        <ErrorPage error={error} />
      </ConnectedPlumaProvider>
    </ThemeContextProvider>
  );
};

type LegacyRedirectReporterProps = { children: React.ReactNode };

function LegacyRedirectReporter({ children }: LegacyRedirectReporterProps) {
  const { pathname } = useLocation();
  const params = useParams();

  const genericPath = Object.entries(params).reduce((accumulator, [key, value]) => {
    if (value == null) {
      return accumulator;
    }

    return accumulator.replace(value, key);
  }, pathname);

  useEffect(() => {
    sentryCaptureLegacyRedirect(genericPath);
  }, [genericPath]);

  return children;
}

// ── Route tree ───────────────────────────────────────────────────────────────

export function createRouter() {
  installLinkPrefetching();

  return sentryCreateBrowserRouter([
    {
      path: '/',
      element: <Root />,
      errorElement: <RootError />,
      children: [
        {
          path: '/logout/*',
          element: (
            <LegacyRedirectReporter>
              <Journeys />
            </LegacyRedirectReporter>
          ),
        },
        ...journeysLegacyUrls.map((path) => ({
          path,
          element: (
            <LegacyRedirectReporter>
              <JourneysNonBackgroundApp />
            </LegacyRedirectReporter>
          ),
        })),
        ...journeysLegacyUrlsWithAuthRefresh.map((path) => ({
          path,
          element: (
            <LegacyRedirectReporter>
              <JourneysWithAuthRefresh />
            </LegacyRedirectReporter>
          ),
        })),

        {
          path: '/env/*',
          element: (
            <LegacyRedirectReporter>
              <RedirectRegexToTargetApp regex={preHydraJourneysEnvRegex} targetApp={AppBundle.Journeys} />
            </LegacyRedirectReporter>
          ),
        },
        {
          path: '/journeys/*',
          element: <RedirectRegexToTargetApp regex={v1HydraJourneysRegex} targetApp={AppBundle.Journeys} />,
        },
        {
          path: '/pipelines/workspaces/:workspaceId/*',
          element: <PipelinesLegacyRedirect />,
        },
        {
          id: 'oauth2',
          path: '/oauth2/*',
          element: (
            <Suspense fallback={<PageLoader />}>
              <OAuth2 />
            </Suspense>
          ),
        },
        {
          id: 'cli',
          path: '/cli',
          element: (
            <Suspense fallback={<PageLoader />}>
              <CliPage />
            </Suspense>
          ),
        },
        {
          id: 'dns-setup',
          path: '/cli/dns-setup',
          element: (
            <Suspense fallback={<PageLoader />}>
              <DnsSetupPage />
            </Suspense>
          ),
        },
        {
          id: 'authenticated',
          element: <Authenticated />,
          children: [
            {
              id: 'sample-workspace',
              path: `/sample-workspace/*`,
              loader: async () => {
                const { workspaceId } = await queryClient.fetchQuery({
                  queryKey: ['sampleAccounts'],
                  queryFn: getSampleAccounts,
                });

                return redirect(generatePath('/workspaces/:workspaceId/journeys', { workspaceId }));
              },
              element: <Journeys />,
            },
            {
              id: 'index',
              element: <Index />,
              index: true,
            },
            {
              id: 'settings_api_keys',
              path: 'settings/api-keys',
              element: (
                <ProtectedRoute permission="account/api_credentials/view" redirectTo="/settings">
                  <AccountApiKeysPage />
                </ProtectedRoute>
              ),
            },
            {
              id: 'settings_audit_logs',
              path: 'settings/audit-logs',
              element: (
                <ProtectedRoute permission="account/view" redirectTo="/settings" allowReadOnlyMasqAccess={true}>
                  <AuditLogsPage />
                </ProtectedRoute>
              ),
            },
            {
              id: 'settings',
              path: 'settings/*',
              element: <Journeys />,
            },
            {
              id: 'workspaces_index',
              path: 'workspaces',
              element: <Index />,
            },
            {
              // /workspaces/:workspaceId — owns the workspace-switch lifecycle.
              // The cache-clear logic lives in `WorkspaceRoot`'s `useEffect`
              // (see its JSDoc) so it runs after React commits the new
              // workspace tree, avoiding a refetch storm of the old
              // workspace's data on its way out.
              id: 'workspace-root',
              path: 'workspaces/:workspaceId',
              element: <WorkspaceRoot />,
              children: [
                {
                  id: 'workspace_index',
                  index: true,
                  element: <Index />,
                },
                {
                  id: 'workspace_settings_ai_business_profile',
                  path: 'settings/ai-business-profile',
                  element: (
                    <LastWorkspaceResolver>
                      <AIBusinessProfile />
                    </LastWorkspaceResolver>
                  ),
                },
                {
                  id: 'workspace_settings_audit_logs',
                  path: 'settings/audit-logs',
                  element: (
                    <LastWorkspaceResolver>
                      <ProtectedRoute
                        permission="account/view"
                        redirectTo={(params) =>
                          generatePath('/workspaces/:workspaceId/settings', { workspaceId: params.workspaceId ?? 0 })
                        }
                        allowReadOnlyMasqAccess={true}
                      >
                        <WorkspaceAuditLogsPage />
                      </ProtectedRoute>
                    </LastWorkspaceResolver>
                  ),
                },
                {
                  id: 'workspace_settings_api_keys',
                  path: 'settings/api-keys',
                  element: (
                    <LastWorkspaceResolver>
                      <ProtectedRoute
                        permission="account/api_credentials/view"
                        redirectTo={(params) =>
                          generatePath('/workspaces/:workspaceId/settings', { workspaceId: params.workspaceId ?? 0 })
                        }
                      >
                        <WorkspaceApiKeysPage />
                      </ProtectedRoute>
                    </LastWorkspaceResolver>
                  ),
                },
                {
                  id: 'workspace_settings_suppressions',
                  path: 'settings/suppressions',
                  element: (
                    <LastWorkspaceResolver>
                      <ProtectedRoute
                        permission="journeys/workspace_settings/view"
                        redirectTo={(params) =>
                          generatePath('/workspaces/:workspaceId/settings', { workspaceId: params.workspaceId ?? 0 })
                        }
                      >
                        <WorkspaceSuppressionsPage />
                      </ProtectedRoute>
                    </LastWorkspaceResolver>
                  ),
                },
                {
                  id: 'workspace_settings_auto_archive',
                  path: 'settings/auto_archive',
                  element: (
                    <FeatureFlagGuard flag={AccountFeatureFlag.SegmentAutoArchive} fallback={NotFoundPage}>
                      <LastWorkspaceResolver>
                        <ProtectedRoute
                          permission="journeys/workspace_settings/view"
                          redirectTo={(params) =>
                            generatePath('/workspaces/:workspaceId/settings', { workspaceId: params.workspaceId ?? 0 })
                          }
                        >
                          <WorkspaceAutoArchivePage />
                        </ProtectedRoute>
                      </LastWorkspaceResolver>
                    </FeatureFlagGuard>
                  ),
                },
                {
                  id: 'workspace_settings_sinch',
                  path: 'settings/actions/sinch',
                  element: (
                    <FeatureFlagGuard flag={AccountFeatureFlag.CustomSmsSinch} fallback={NotFoundPage}>
                      <LastWorkspaceResolver>
                        <ProtectedRoute
                          permission="journeys/workspace_settings/view"
                          redirectTo={(params) =>
                            generatePath('/workspaces/:workspaceId/settings', { workspaceId: params.workspaceId ?? 0 })
                          }
                        >
                          <SinchSettingsPage />
                        </ProtectedRoute>
                      </LastWorkspaceResolver>
                    </FeatureFlagGuard>
                  ),
                },
                // Path-to-path redirects (workspace-scoped — `from` paths are
                // relative to this parent so the leading `workspaces/:workspaceId/`
                // is stripped).
                ...REDIRECTS.map(({ from, to }) => ({
                  id: `redirect_${from.replace(/[/:]/g, '_')}`,
                  path: from.replace(/^workspaces\/:workspaceId\//, ''),
                  loader: ({ params }: LoaderFunctionArgs) =>
                    redirect(to.replace(/:(\w+)/g, (_, key) => params[key] ?? '')),
                })),
                {
                  id: 'workspace_analysis_deliverability',
                  path: 'analysis/deliverability',
                  element: (
                    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
                      <LastWorkspaceResolver>
                        <DeliverabilityPage />
                      </LastWorkspaceResolver>
                    </PlanGuard>
                  ),
                },
                {
                  id: 'workspace_hydra_agent',
                  path: 'agent',
                  element: (
                    <AiEnabledGuard fallback={AiNotEnabledFallback}>
                      <LastWorkspaceResolver>
                        <Agent />
                      </LastWorkspaceResolver>
                    </AiEnabledGuard>
                  ),
                },
                {
                  id: 'workspace_hydra_agent_routine',
                  path: 'agent/routines',
                  element: (
                    <AiEnabledGuard fallback={AiNotEnabledFallback}>
                      <LastWorkspaceResolver>
                        <RoutinesPage />
                      </LastWorkspaceResolver>
                    </AiEnabledGuard>
                  ),
                },
                {
                  id: 'workspace_hydra_agent_skills',
                  path: 'agent/skills',
                  element: (
                    <AiEnabledGuard fallback={AiNotEnabledFallback}>
                      <LastWorkspaceResolver>
                        <SkillsPage />
                      </LastWorkspaceResolver>
                    </AiEnabledGuard>
                  ),
                },
                {
                  id: 'workspace_hydra_agent_skill_new',
                  path: 'agent/skills/new',
                  element: (
                    <AiEnabledGuard fallback={AiNotEnabledFallback}>
                      <LastWorkspaceResolver>
                        <NewSkillPage />
                      </LastWorkspaceResolver>
                    </AiEnabledGuard>
                  ),
                },
                {
                  id: 'workspace_hydra_agent_skill_detail',
                  path: 'agent/skills/:skillPath',
                  element: (
                    <AiEnabledGuard fallback={AiNotEnabledFallback}>
                      <LastWorkspaceResolver>
                        <SkillDetailPage />
                      </LastWorkspaceResolver>
                    </AiEnabledGuard>
                  ),
                },
                {
                  id: 'workspace_home',
                  path: 'home',
                  element: (
                    <LastWorkspaceResolver>
                      <HomePage />
                    </LastWorkspaceResolver>
                  ),
                },
                {
                  id: 'workspace_add_people',
                  path: 'home/add-people',
                  element: (
                    <LastWorkspaceResolver>
                      <AddPeoplePage />
                    </LastWorkspaceResolver>
                  ),
                },
                {
                  id: 'workspace_health',
                  path: 'health',
                  element: (
                    <LastWorkspaceResolver>
                      <HealthPage />
                    </LastWorkspaceResolver>
                  ),
                },
                // Dev-only: condition viewer preview page
                ...(import.meta.env.DEV
                  ? [
                      {
                        id: 'dev_condition_viewer',
                        path: 'dev/condition-viewer',
                        async lazy() {
                          const { ConditionViewerDevPage } =
                            await import('@/features/condition-viewer/dev/condition-viewer-dev-page');
                          return { Component: ConditionViewerDevPage };
                        },
                      },
                      {
                        id: 'dev_tq_campaigns_demo',
                        path: 'dev/db/campaigns',
                        async lazy() {
                          const { CampaignsDevPage } = await import('@/pages/dev/db/campaigns-dev-page');
                          return { Component: CampaignsDevPage };
                        },
                      },
                      {
                        id: 'dev_tq_action_detail',
                        path: 'dev/db/campaigns/actions/:actionId',
                        async lazy() {
                          const { ActionDevPage } = await import('@/pages/dev/db/action-dev-page');
                          return { Component: ActionDevPage };
                        },
                      },
                    ]
                  : []),
                // ── Geofences (native Hydra routes) ───────────────────
                {
                  id: 'geofences',
                  path: 'geofences',
                  element: (
                    <FeatureFlagGuard flag={AccountFeatureFlag.GeoFencingEnabled} fallback={NotFoundPage}>
                      <LastWorkspaceResolver>
                        <ProtectedRoute
                          permission="journeys/geofences/view"
                          redirectTo={(params) =>
                            generatePath('/workspaces/:workspaceId/journeys', { workspaceId: params.workspaceId ?? 0 })
                          }
                        >
                          <Suspense fallback={<PageLoader />}>
                            <GeofencesIndexPage />
                          </Suspense>
                        </ProtectedRoute>
                      </LastWorkspaceResolver>
                    </FeatureFlagGuard>
                  ),
                },
                {
                  id: 'geofences_new',
                  path: 'geofences/new',
                  element: (
                    <FeatureFlagGuard flag={AccountFeatureFlag.GeoFencingEnabled} fallback={NotFoundPage}>
                      <LastWorkspaceResolver>
                        <ProtectedRoute
                          permission="journeys/geofences/create"
                          redirectTo={(params) =>
                            generatePath('/workspaces/:workspaceId/geofences', {
                              workspaceId: params.workspaceId ?? 0,
                            })
                          }
                        >
                          <Suspense fallback={<PageLoader />}>
                            <GeofencesCreatePage />
                          </Suspense>
                        </ProtectedRoute>
                      </LastWorkspaceResolver>
                    </FeatureFlagGuard>
                  ),
                },
                {
                  id: 'geofences_edit',
                  path: 'geofences/:geofenceId',
                  element: (
                    <FeatureFlagGuard flag={AccountFeatureFlag.GeoFencingEnabled} fallback={NotFoundPage}>
                      <LastWorkspaceResolver>
                        <ProtectedRoute
                          permission="journeys/geofences/edit"
                          redirectTo={(params) =>
                            generatePath('/workspaces/:workspaceId/geofences', {
                              workspaceId: params.workspaceId ?? 0,
                            })
                          }
                        >
                          <Suspense fallback={<PageLoader />}>
                            <GeofencesEditPage />
                          </Suspense>
                        </ProtectedRoute>
                      </LastWorkspaceResolver>
                    </FeatureFlagGuard>
                  ),
                },
                {
                  id: 'workspace_assets',
                  path: 'assets',
                  element: (
                    <LastWorkspaceResolver>
                      <Suspense fallback={<PageLoader />}>
                        <AssetsPage />
                      </Suspense>
                    </LastWorkspaceResolver>
                  ),
                },
                {
                  id: 'workspace_snippets_layout',
                  path: 'snippets',
                  // `LastWorkspaceResolver` is what every other Hydra-owned
                  // workspace page (Home, Health, Agent, Settings…) uses to
                  // resolve `/workspaces/last/...` → the user's last-used
                  // workspace id. `HYDRA_ONLY_URLS_REGEX` already accepts
                  // `last` in the snippets pattern, so without this wrap
                  // `requireWorkspaceId()` would throw on
                  // `/workspaces/last/snippets`.
                  element: (
                    <LastWorkspaceResolver>
                      <Suspense fallback={<PageLoader />}>
                        <SnippetsLayout />
                      </Suspense>
                    </LastWorkspaceResolver>
                  ),
                  children: [
                    { id: 'workspace_snippets', index: true, element: <SnippetsPage /> },
                    { id: 'workspace_snippet_new', path: 'new', element: <NewSnippetPage /> },
                    { id: 'workspace_snippet_detail', path: ':snippetId', element: <SnippetDetailPage /> },
                  ],
                },
                {
                  id: 'workspace_settings',
                  path: 'settings/*',
                  element: <Journeys />,
                },
                {
                  id: AppBundle.Journeys,
                  path: `${AppBundleRoutes[AppBundle.Journeys].replace(/^\//, '')}/*`,
                  element: <Journeys />,
                },
                // ── Pipeline routes (native, not an app bundle) ─────────
                {
                  id: 'pipeline-sources-add',
                  path: 'pipelines/sources/add',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <PipelineAddSourcePage />
                    </Suspense>
                  ),
                },
                {
                  id: 'pipeline-sources-add-option',
                  path: 'pipelines/sources/add/:sourceOptionId',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <PipelineAddSourceOptionPage />
                    </Suspense>
                  ),
                },
                {
                  id: 'pipeline-sources-journeys',
                  path: 'pipelines/sources/journeys',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <PipelineSourcesJourneysPage />
                    </Suspense>
                  ),
                },
                {
                  id: 'pipeline-source-sync-add',
                  path: 'pipelines/sources/:sourceId/syncs/add',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <PipelineSourceAddSyncPage />
                    </Suspense>
                  ),
                },
                {
                  id: 'pipeline-source-sync-edit',
                  path: 'pipelines/sources/:sourceId/syncs/:syncId',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <PipelineSourceAddSyncPage />
                    </Suspense>
                  ),
                },
                {
                  id: 'pipeline-source-overview',
                  path: 'pipelines/sources/:sourceId',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <PipelineSourceLayoutPage />
                    </Suspense>
                  ),
                  children: [
                    { index: true, element: <PipelineSourceIndexPage /> },
                    { path: 'data-in', element: <PipelineSourceDataInPage /> },
                    { path: 'imports', element: <PipelineSourceSyncRunsPage /> },
                    { path: 'syncs', element: <PipelineSourceSyncsPage /> },
                    { path: 'databases', element: <PipelineSourceDatabasesPage /> },
                    { path: 'settings', element: <PipelineSourceSettingsPage /> },
                  ],
                },
                {
                  id: 'pipeline-destinations-add',
                  path: 'pipelines/destinations/add',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <PipelineAddDestinationPage />
                    </Suspense>
                  ),
                },
                {
                  id: 'pipeline-destinations-add-option',
                  path: 'pipelines/destinations/add/:destinationOptionId',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <PipelineAddDestinationOptionPage />
                    </Suspense>
                  ),
                },
                {
                  id: 'pipeline-destinations-journeys',
                  path: 'pipelines/destinations/journeys',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <PipelineDestinationsJourneysPage />
                    </Suspense>
                  ),
                },
                {
                  id: 'pipeline-destination-overview',
                  path: 'pipelines/destinations/:destinationId',
                  element: (
                    <Suspense fallback={<PageLoader />}>
                      <PipelineDestinationLayoutPage />
                    </Suspense>
                  ),
                  children: [
                    { index: true, element: <PipelineDestinationIndexPage /> },
                    { path: 'data-out', element: <PipelineDestinationDataOutPage /> },
                    { path: 'exports', element: <PipelineDestinationExportsPage /> },
                    {
                      path: 'subscriptions',
                      children: [
                        { index: true, element: <PipelineDestinationSubscriptionsIndexPage /> },
                        { path: 'add', element: <PipelineDestinationSubscriptionsAddPage /> },
                        { path: ':subscriptionId', element: <PipelineDestinationSubscriptionPage /> },
                      ],
                    },
                    { path: 'tester', element: <PipelineDestinationTesterPage /> },
                    { path: 'settings', element: <PipelineDestinationSettingsPage /> },
                  ],
                },
                {
                  id: 'parcel',
                  path: 'parcel/*',
                  element: <RedirectRegexToTargetApp regex={oldParcelRegex} targetApp={AppBundle.DesignStudio} />,
                },
                {
                  id: AppBundle.DesignStudio,
                  path: `${AppBundleRoutes[AppBundle.DesignStudio].replace(/^\//, '')}/*`,
                  element: (
                    <LastWorkspaceResolver appBundle={AppBundle.DesignStudio}>
                      <DesignStudio />
                    </LastWorkspaceResolver>
                  ),
                },
              ],
            },
            {
              id: 'internal-tools',
              path: `internal-tools/*`,
              element: <Journeys />,
            },
            {
              id: 'trial-expired',
              path: `/trial-expired${AppBundleRoutes[AppBundle.Journeys]}`,
              element: <Journeys />,
            },
            {
              id: 'not-found',
              path: '*',
              element: <NotFoundPage />,
            },
          ],
        },
      ],
    },
    {
      path: '/design-studio-embed',
      element: <EmbedRoot />,
      errorElement: <RootError />,
      children: [
        {
          id: 'design-studio-embed-editor',
          path: 'cio/*',
          element: <DesignStudioNonBackgroundApp />,
        },
      ],
    },
  ]);
}
